/**
 * Currency formatting utilities for price and cost fields
 */

/**
 * Formats a numeric value as currency string with dollar sign
 * @param value - The numeric value to format
 * @returns Formatted currency string (e.g., "$12.34")
 */
export const formatCurrency = (value: number): string => `$${value.toFixed(2)}`;

/**
 * Parses a currency string to a numeric value
 * @param value - The currency string to parse (e.g., "$12.34")
 * @returns The numeric value
 */
export const parseCurrency = (value: string): number => {
  const cleaned = value.replace(/[^0-9.]/g, '');
  return parseFloat(cleaned) || 0;
};

/**
 * Formats user input for currency fields with left-to-right entry
 * @param input - The raw input string
 * @param previousValue - The previous formatted value
 * @returns Formatted currency string
 */
export const formatCurrencyInput = (input: string, previousValue?: string): string => {
  // Remove all non-digit characters
  const digits = input.replace(/\D/g, '');

  // If no digits, return $0.00
  if (!digits) {
    return '$0.00';
  }

  // Convert to cents and format
  const cents = parseInt(digits, 10);
  const dollars = cents / 100;

  return `$${dollars.toFixed(2)}`;
};

/**
 * Handles backspace/delete for currency input with right-to-left deletion
 * @param currentValue - The current formatted value (e.g., "$12.34")
 * @returns New formatted value after deletion
 */
export const handleCurrencyDelete = (currentValue: string): string => {
  // Extract digits only
  const digits = currentValue.replace(/\D/g, '');

  // If no digits or only one digit, return $0.00
  if (digits.length <= 1) {
    return '$0.00';
  }

  // Remove the last digit
  const newDigits = digits.slice(0, -1);
  const cents = parseInt(newDigits, 10);
  const dollars = cents / 100;

  return `$${dollars.toFixed(2)}`;
};

/**
 * Processes keyboard input for currency fields
 * @param event - The keyboard event
 * @param currentValue - The current field value
 * @returns Object with new value and whether to prevent default
 */
export const processCurrencyInput = (
  event: React.KeyboardEvent<HTMLInputElement>,
  currentValue: string
): {
  newValue: string;
  preventDefault: boolean;
} => {
  const { key } = event;

  // Handle backspace/delete
  if (key === 'Backspace' || key === 'Delete') {
    return {
      newValue: handleCurrencyDelete(currentValue),
      preventDefault: true,
    };
  }

  // Only allow digits
  if (!/^\d$/.test(key)) {
    return {
      newValue: currentValue,
      preventDefault: true,
    };
  }

  // Add the digit
  const newInput = currentValue + key;
  return {
    newValue: formatCurrencyInput(newInput),
    preventDefault: true,
  };
};
